/* eslint-disable max-len */
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { cx } from 'emotion';
import { bool, number, string } from 'prop-types';
import React, { memo } from 'react';

import { BACK_CARD_NAME } from '@ezugi/constants';
import { Card } from '@ezugi/primitives';
import { DeviceInfo } from '@ezugi/utils';

import {
  animationDelayVar,
  highlightStyle,
  iosHighlightStyle,
  mobileSVGStyle,
  animatedBase,
  svgStyle,
} from './style.css';

type CardMultiplierProps = {
  cardName: string;
  animated?: boolean;
  animationDelay?: number;
  value: number;
  isHandheld?: boolean;
  scope?: string;
  highlight?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

function CardMultiplier({
  cardName,
  highlight,
  animated,
  animationDelay,
  value,
  isHandheld,
  scope,
  className,
  style = {},
}: CardMultiplierProps) {
  return (
    <svg
      width="54"
      height="88"
      viewBox="0 0 54 88"
      fill="none"
      className={cx(className, svgStyle, {
        [animatedBase]: animated,
        [highlightStyle]: !DeviceInfo.isIOS && animated && highlight,
        [iosHighlightStyle]: !!DeviceInfo.isIOS && animated && highlight,
        [mobileSVGStyle]: isHandheld,
      })}
      style={{
        ...assignInlineVars({
          [animationDelayVar]: `${animationDelay}s`,
        }),
        ...style,
      }}
    >
      <path
        fill={`url(#a-${scope}-${cardName})`}
        fillOpacity="0.82"
        d="M2.17866 13.1895C2.13206 10.7056 3.66332 8.46453 5.9943 7.60518L24.5825 0.752401C25.8852 0.272134 27.3165 0.272133 28.6192 0.752401L47.2074 7.60518C49.5383 8.46452 51.0696 10.7056 51.023 13.1895L49.9734 69.1375C49.9322 71.3341 48.6606 73.3214 46.6834 74.2793L29.1448 82.7759C27.5381 83.5542 25.6635 83.5542 24.0569 82.7759L6.51825 74.2793C4.54102 73.3214 3.26943 71.3341 3.22822 69.1375L2.17866 13.1895Z"
      />

      <g filter={`url(#b-${scope}-${cardName})`} x="0.0789719" y="0.394753" width="56.8421" height="90.9382">
        {highlight
          ? (
            <path
              fill={`url(#active-${scope}-${cardName})`}
              stroke="url(#multiplierStroke)"
              d="M2.1942 14.0178C2.1383 11.038 3.97527 8.34952 6.77163 7.3186L24.1795 0.900961C25.7423 0.324808 27.4594 0.324807 29.0222 0.900961L46.43 7.3186C49.2264 8.34952 51.0634 11.038 51.0075 14.0178L49.9868 68.4226C49.9374 71.0578 48.4119 73.4419 46.04 74.591L29.6527 82.5298C27.7253 83.4636 25.4764 83.4636 23.5489 82.5298L7.1617 74.591C4.78971 73.4419 3.26425 71.0578 3.21481 68.4226L2.1942 14.0178Z"
              shapeRendering="crispEdges"
            />
          )
          : (
            <path
              fill="black"
              stroke="url(#multiplierStroke)"
              d="M2.1942 14.0178C2.1383 11.038 3.97527 8.34952 6.77163 7.3186L24.1795 0.900961C25.7423 0.324808 27.4594 0.324807 29.0222 0.900961L46.43 7.3186C49.2264 8.34952 51.0634 11.038 51.0075 14.0178L49.9868 68.4226C49.9374 71.0578 48.4119 73.4419 46.04 74.591L29.6527 82.5298C27.7253 83.4636 25.4764 83.4636 23.5489 82.5298L7.1617 74.591C4.78971 73.4419 3.26425 71.0578 3.21481 68.4226L2.1942 14.0178Z"
              shapeRendering="crispEdges"
            />
          )}
      </g>
      <path fill={`url(#d-${scope}-${cardName})`} fillOpacity="0" d="M8.077 15.142a5.835 5.835 0 0 1 3.808-5.58L32.973 1.75a5.835 5.835 0 0 1 4.054 0l21.088 7.81a5.835 5.835 0 0 1 3.808 5.58L60.75 77.927a5.835 5.835 0 0 1-3.28 5.138l-19.916 9.693a5.835 5.835 0 0 1-5.108 0L12.53 83.064a5.835 5.835 0 0 1-3.28-5.138L8.077 15.142Z" />
      <g filter={`url(#e-${scope}-${cardName})`}>
        <mask id={`h-${scope}-${cardName}`} fill="#fff">
          <path
            fillRule="evenodd"
            d="M4.17841 13.1518L4.80789 46.7068C4.82077 47.3937 5.28011 47.9919 5.94039 48.1816L6.03236 48.208C7.5162 48.6344 7.5162 50.737 6.03236 51.1634V51.1634C5.36314 51.3557 4.90669 51.9738 4.91975 52.67L5.22797 69.0999C5.25505 70.5436 6.0908 71.8497 7.39032 72.4793L24.9289 80.9759C25.9849 81.4874 27.217 81.4874 28.2729 80.9759L45.8115 72.4793C47.1111 71.8497 47.9468 70.5436 47.9739 69.0999L48.2821 52.6698C48.2952 51.9736 47.8387 51.3555 47.1695 51.1632V51.1632C45.6856 50.7369 45.6856 48.6342 47.1695 48.2078L47.2614 48.1814C47.9217 47.9916 48.3811 47.3935 48.394 46.7066L49.0234 13.1518C49.0541 11.5193 48.0477 10.0464 46.5156 9.48161L27.9275 2.62883C27.0713 2.31318 26.1306 2.31318 25.2744 2.62883L6.68621 9.48161C5.15419 10.0464 4.14778 11.5193 4.17841 13.1518ZM5.9944 7.60507C3.66342 8.46441 2.13216 10.7054 2.17876 13.1893L3.22832 69.1374C3.26953 71.334 4.54112 73.3213 6.51835 74.2792L24.057 82.7758C25.6636 83.5541 27.5382 83.5541 29.1449 82.7758L46.6835 74.2792C48.6607 73.3213 49.9323 71.334 49.9735 69.1374L51.0231 13.1893C51.0697 10.7054 49.5384 8.46441 47.2075 7.60507L28.6193 0.75229C27.3166 0.272023 25.8853 0.272023 24.5826 0.752291L5.9944 7.60507Z"
            clipRule="evenodd"
          />
        </mask>
        <path
          fill={`url(#f-${scope}-${cardName})`}
          fillOpacity="0.3"
          fillRule="evenodd"
          d="M4.17841 13.1518L4.80789 46.7068C4.82077 47.3937 5.28011 47.9919 5.94039 48.1816L6.03236 48.208C7.5162 48.6344 7.5162 50.737 6.03236 51.1634V51.1634C5.36314 51.3557 4.90669 51.9738 4.91975 52.67L5.22797 69.0999C5.25505 70.5436 6.0908 71.8497 7.39032 72.4793L24.9289 80.9759C25.9849 81.4874 27.217 81.4874 28.2729 80.9759L45.8115 72.4793C47.1111 71.8497 47.9468 70.5436 47.9739 69.0999L48.2821 52.6698C48.2952 51.9736 47.8387 51.3555 47.1695 51.1632V51.1632C45.6856 50.7369 45.6856 48.6342 47.1695 48.2078L47.2614 48.1814C47.9217 47.9916 48.3811 47.3935 48.394 46.7066L49.0234 13.1518C49.0541 11.5193 48.0477 10.0464 46.5156 9.48161L27.9275 2.62883C27.0713 2.31318 26.1306 2.31318 25.2744 2.62883L6.68621 9.48161C5.15419 10.0464 4.14778 11.5193 4.17841 13.1518ZM5.9944 7.60507C3.66342 8.46441 2.13216 10.7054 2.17876 13.1893L3.22832 69.1374C3.26953 71.334 4.54112 73.3213 6.51835 74.2792L24.057 82.7758C25.6636 83.5541 27.5382 83.5541 29.1449 82.7758L46.6835 74.2792C48.6607 73.3213 49.9323 71.334 49.9735 69.1374L51.0231 13.1893C51.0697 10.7054 49.5384 8.46441 47.2075 7.60507L28.6193 0.75229C27.3166 0.272023 25.8853 0.272023 24.5826 0.752291L5.9944 7.60507Z"
          clipRule="evenodd"
          shapeRendering="crispEdges"
        />
        <path
          fill={`url(#g-${scope}-${cardName})`}
          d="M4.17841 13.1518L6.17806 13.1143L4.17841 13.1518ZM5.22797 69.0999L3.22832 69.1374L5.22797 69.0999ZM7.39032 72.4793L8.26228 70.6794H8.26228L7.39032 72.4793ZM24.9289 80.9759L24.057 82.7758H24.057L24.9289 80.9759ZM28.2729 80.9759L29.1449 82.7758H29.1449L28.2729 80.9759ZM45.8115 72.4793L46.6835 74.2792L45.8115 72.4793ZM47.9739 69.0999L49.9735 69.1374L47.9739 69.0999ZM49.0234 13.1518L51.0231 13.1893V13.1893L49.0234 13.1518ZM46.5156 9.48161L47.2075 7.60507V7.60507L46.5156 9.48161ZM27.9275 2.62883L28.6193 0.75229V0.75229L27.9275 2.62883ZM25.2744 2.62883L25.9662 4.50537L25.9662 4.50537L25.2744 2.62883ZM6.68621 9.48161L7.37802 11.3581H7.37802L6.68621 9.48161ZM2.17876 13.1893L4.17841 13.1518L2.17876 13.1893ZM5.9944 7.60507L6.68621 9.48161H6.68621L5.9944 7.60507ZM3.22832 69.1374L5.22797 69.0998L3.22832 69.1374ZM6.51835 74.2792L7.39032 72.4793H7.39032L6.51835 74.2792ZM24.057 82.7758L23.185 84.5757H23.185L24.057 82.7758ZM29.1449 82.7758L30.0169 84.5757H30.0169L29.1449 82.7758ZM46.6835 74.2792L45.8115 72.4793L46.6835 74.2792ZM49.9735 69.1374L51.9732 69.1749L49.9735 69.1374ZM51.0231 13.1893L53.0227 13.2269V13.2269L51.0231 13.1893ZM47.2075 7.60507L46.5156 9.48161L47.2075 7.60507ZM28.6193 0.75229L29.3111 -1.12425V-1.12425L28.6193 0.75229ZM24.5826 0.752291L23.8907 -1.12425V-1.12425L24.5826 0.752291ZM47.2614 48.1814L46.7091 46.2591L47.2614 48.1814ZM48.394 46.7066L50.3936 46.7441L48.394 46.7066ZM5.94039 48.1816L6.49273 46.2594L5.94039 48.1816ZM47.1695 48.2078L46.6171 46.2856L47.1695 48.2078ZM6.03236 48.208L5.48002 50.1303L6.03236 48.208ZM48.2821 52.6698L46.2825 52.6323L48.2821 52.6698ZM6.80754 46.6693L6.17806 13.1143L2.17876 13.1893L2.80824 46.7444L6.80754 46.6693ZM6.5847 46.2858L6.49273 46.2594L5.38805 50.1038L5.48002 50.1303L6.5847 46.2858ZM7.22762 69.0623L6.9194 52.6324L2.9201 52.7075L3.22832 69.1374L7.22762 69.0623ZM8.26228 70.6794C7.64048 70.3781 7.24058 69.7532 7.22762 69.0623L3.22832 69.1374C3.26953 71.334 4.54112 73.3213 6.51836 74.2792L8.26228 70.6794ZM25.8009 79.1759L8.26228 70.6794L6.51835 74.2792L24.057 82.7758L25.8009 79.1759ZM27.401 79.1759C26.8957 79.4207 26.3062 79.4207 25.8009 79.1759L24.057 82.7758C25.6636 83.5541 27.5382 83.5541 29.1449 82.7758L27.401 79.1759ZM44.9396 70.6794L27.401 79.1759L29.1449 82.7758L46.6835 74.2792L44.9396 70.6794ZM45.9742 69.0623C45.9613 69.7532 45.5614 70.3781 44.9396 70.6794L46.6835 74.2792C48.6607 73.3213 49.9323 71.334 49.9735 69.1374L45.9742 69.0623ZM46.2825 52.6323L45.9742 69.0623L49.9735 69.1374L50.2818 52.7073L46.2825 52.6323ZM47.7218 50.13L47.8138 50.1036L46.7091 46.2591L46.6171 46.2856L47.7218 50.13ZM47.0238 13.1143L46.3943 46.669L50.3936 46.7441L51.0231 13.1893L47.0238 13.1143ZM45.8238 11.3581C46.5569 11.6284 47.0385 12.3332 47.0238 13.1143L51.0231 13.1893C51.0697 10.7054 49.5384 8.46441 47.2075 7.60507L45.8238 11.3581ZM27.2357 4.50537L45.8238 11.3581L47.2075 7.60507L28.6193 0.75229L27.2357 4.50537ZM25.9662 4.50537C26.3759 4.35433 26.826 4.35433 27.2357 4.50537L28.6193 0.75229C27.3166 0.272023 25.8853 0.272023 24.5826 0.752291L25.9662 4.50537ZM7.37802 11.3581L25.9662 4.50537L24.5826 0.752291L5.9944 7.60507L7.37802 11.3581ZM6.17806 13.1143C6.1634 12.3332 6.64496 11.6284 7.37802 11.3581L5.9944 7.60507C3.66342 8.46441 2.13216 10.7054 2.17876 13.1893L6.17806 13.1143ZM4.17841 13.1518C4.14778 11.5193 5.15419 10.0464 6.68621 9.48161L5.30258 5.72853C2.17265 6.88242 0.116545 9.89159 0.179113 13.2269L4.17841 13.1518ZM5.22797 69.0998L4.17841 13.1518L0.179113 13.2269L1.22867 69.1749L5.22797 69.0998ZM7.39032 72.4793C6.0908 71.8497 5.25505 70.5436 5.22797 69.0998L1.22867 69.1749C1.28401 72.1244 2.99144 74.7929 5.64639 76.0791L7.39032 72.4793ZM24.9289 80.9759L7.39032 72.4793L5.64638 76.0791L23.185 84.5757L24.9289 80.9759ZM28.2729 80.9759C27.217 81.4874 25.9849 81.4874 24.9289 80.9759L23.185 84.5757C25.3424 85.6208 27.8595 85.6208 30.0169 84.5757L28.2729 80.9759ZM45.8115 72.4793L28.2729 80.9759L30.0169 84.5757L47.5555 76.0791L45.8115 72.4793ZM47.9739 69.0998C47.9468 70.5436 47.1111 71.8497 45.8115 72.4793L47.5555 76.0791C50.2104 74.7929 51.9179 72.1244 51.9732 69.1749L47.9739 69.0998ZM49.0234 13.1518L47.9739 69.0998L51.9732 69.1749L53.0227 13.2269L49.0234 13.1518ZM46.5156 9.48161C48.0477 10.0464 49.0541 11.5193 49.0234 13.1518L53.0227 13.2269C53.0853 9.89158 51.0292 6.88242 47.8993 5.72853L46.5156 9.48161ZM27.9275 2.62883L46.5156 9.48161L47.8993 5.72853L29.3111 -1.12425L27.9275 2.62883ZM25.2744 2.62883C26.1306 2.31318 27.0713 2.31318 27.9275 2.62883L29.3111 -1.12425C27.5619 -1.76913 25.64 -1.76913 23.8907 -1.12425L25.2744 2.62883ZM6.68621 9.48161L25.2744 2.62883L23.8907 -1.12425L5.30258 5.72853L6.68621 9.48161ZM47.8138 50.1036C49.3179 49.6714 50.3643 48.3088 50.3936 46.7441L46.3943 46.669C46.3979 46.4781 46.5256 46.3119 46.7091 46.2591L47.8138 50.1036ZM2.80824 46.7444C2.83759 48.309 3.88396 49.6716 5.38805 50.1038L6.49273 46.2594C6.67627 46.3121 6.80395 46.4784 6.80754 46.6693L2.80824 46.7444ZM47.7218 49.241C47.7903 49.2607 47.8942 49.3221 47.9708 49.431C48.0372 49.5255 48.0566 49.6167 48.0566 49.6855C48.0566 49.7543 48.0372 49.8455 47.9708 49.94C47.8942 50.0489 47.7903 50.1103 47.7218 50.13L46.6171 46.2856C43.203 47.2666 43.2031 52.1044 46.6171 53.0855L47.7218 49.241ZM5.48002 49.2412C3.94026 49.6836 2.89006 51.1057 2.9201 52.7075L6.9194 52.6324C6.92333 52.8419 6.78602 53.0278 6.5847 53.0856L5.48002 49.2412ZM5.48002 50.1303C5.41149 50.1106 5.30767 50.0492 5.23107 49.9402C5.16464 49.8458 5.14524 49.7546 5.14524 49.6857C5.14524 49.6169 5.16464 49.5257 5.23107 49.4312C5.30767 49.3223 5.41149 49.2609 5.48002 49.2412L6.5847 53.0856C9.99875 52.1046 9.99876 47.2668 6.5847 46.2858L5.48002 50.1303ZM50.2818 52.7073C50.3118 51.1055 49.2616 49.6835 47.7218 49.241L46.6171 53.0855C46.4158 53.0276 46.2785 52.8417 46.2825 52.6323L50.2818 52.7073Z"
          mask={`url(#h-${scope}-${cardName})`}
        />
      </g>
      <g filter={`url(#i-${scope}-${cardName})`} key={`${cardName}`}>
        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <Card type={Card.CARD_TYPES.SMALL} name={cardName} value={value} x={14} y={37} width={27} height={37} />
        </g>
      </g>
      <g filter="url(#filter)">
        <text
          x="18"
          y="30"
          fontSize="24"
          strokeWidth="3"
          stroke="url(#stroke)"
          textAnchor="middle"
          fontFamily="Montserrat"
          fontWeight="600"
          fill="url(#fill)"
          paintOrder="stroke"
        >
          {value}
        </text>
        <text
          x="35"
          y="30"
          fontSize="24"
          strokeWidth="3"
          stroke="url(#stroke)"
          textAnchor="middle"
          fontFamily="Montserrat"
          fontWeight="600"
          fill="url(#fill)"
          paintOrder="stroke"
        >
          x
        </text>
      </g>
      <defs>
        <filter id="filter" x="0.36644" y="0.299999" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2739_54421" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.1125 0 0 0 0 0.054 0 0 0 0 0 0 0 0 1 0" />
          <feBlend mode="normal" in2="effect1_dropShadow_2739_54421" result="effect2_dropShadow_2739_54421" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_2739_54421" result="shape" />
        </filter>
        <linearGradient id="fill" gradientTransform="rotate(90)">
          <stop stopColor="#FFEECC" />
          <stop offset="0.360417" stopColor="#E0C275" />
          <stop offset="0.498333" stopColor="#9F753C" />
          <stop offset="0.458433" stopColor="#3E1A09" />
          <stop offset="0.78" stopColor="#E7BA63" />
          <stop offset="1" stopColor="#FDF2D9" />
        </linearGradient>
        <linearGradient id="stroke" gradientTransform="rotate(90)">
          <stop offset="0.161458" stopColor="#FFEBD3" />
          <stop offset="0.411458" stopColor="#F5D695" />
          <stop offset="0.471667" stopColor="#BA8657" />
          <stop offset="0.585417" stopColor="#E8A138" />
          <stop offset="1" stopColor="#FFEED3" />
        </linearGradient>
        <linearGradient id={`active-${scope}-${cardName}`} x1="28.5" y1="3" x2="28.5" y2="87" gradientUnits="userSpaceOnUse">
          <stop stopColor="#140D00" />
          <stop offset="0.58659" stopColor="#A27726" />
          <stop offset="0.588406" />
          <stop offset="1" stopColor="#946A1A" />
        </linearGradient>
        <linearGradient id={`inactive-${scope}-${cardName}`} x1="35" x2="35" y1="10" y2="97" gradientUnits="userSpaceOnUse">
          <stop stopColor="#3A2212" />
          <stop offset=".589" stopColor="#664A39" />
          <stop offset="1" stopColor="#BB805F" stopOpacity=".3" />
        </linearGradient>
        <linearGradient id={`a-${scope}-${cardName}`} x1="35" x2="35" y1="10" y2="97" gradientUnits="userSpaceOnUse">
          <stop stopColor="#272727" stopOpacity=".8" />
          <stop offset=".568" stopColor="#616161" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`d-${scope}-${cardName}`} x1="-13.5" x2="35" y1="63.5" y2="97" gradientUnits="userSpaceOnUse">
          <stop stopColor="#fff" stopOpacity="0" />
          <stop offset=".489" stopColor="#fff" stopOpacity=".7" />
          <stop offset=".49" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`f-${scope}-${cardName}`} x1="35" x2="35" y1="11.17" y2="96.28" gradientUnits="userSpaceOnUse">
          <stop stopColor="#8A3200" />
          <stop offset=".589" stopColor="#AE3F00" />
          <stop offset="1" stopColor="#FF7629" stopOpacity=".3" />
        </linearGradient>
        <linearGradient id={`g-${scope}-${cardName}`} x1="35" x2="35" y1="1.387" y2="93.346" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFEBD8" />
          <stop offset=".229" stopColor="#FFC080" />
          <stop offset=".526" stopColor="#D69050" />
          <stop offset=".911" stopColor="#7B470F" />
          <stop offset="1" stopColor="#B58F66" />
        </linearGradient>
        <linearGradient id="multiplierStroke" x1="47.1495" y1="0.383877" x2="47.1495" y2="83.3513" gradientUnits="userSpaceOnUse">
          <stop offset="0.161458" stopColor="#FDF2D9" />
          <stop offset="0.484375" stopColor="#F9DDA3" />
          <stop offset="0.520833" stopColor="#341003" />
          <stop offset="0.890625" stopColor="#F7C96B" />
          <stop offset="1" stopColor="#FDF2D9" />
        </linearGradient>
        <filter id={`b-${scope}-${cardName}`} x="0.0789719" y="0.394753" width="56.8421" height="90.9382" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2507_50989" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2507_50989" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.0916667 0 0 0 0 0.0916667 0 0 0 0 0.0916667 0 0 0 1 0" />
          <feBlend mode="normal" in2="shape" result="effect2_innerShadow_2507_50989" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect3_innerShadow_2507_50989" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="0.5" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0" />
          <feBlend mode="normal" in2="effect2_innerShadow_2507_50989" result="effect3_innerShadow_2507_50989" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="-3" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.25 0 0 0 0 0.132833 0 0 0 0 0.0958333 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="effect3_innerShadow_2507_50989" result="effect4_innerShadow_2507_50989" />
        </filter>
        <filter id={`e-${scope}-${cardName}`} x="0.177727" y="0.39209" width="52.8464" height="86.9675" colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.65 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2648_53902" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2648_53902" result="shape" />
        </filter>
        <filter id={`i-${scope}-${cardName}`} width="43.905" height="43" x="13" y="39" colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation=".5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0.2875 0 0 0 0 0.2875 0 0 0 0 0.2875 0 0 0 0.72 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_326:13147" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_326:13147" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

CardMultiplier.propTypes = {
  isHandheld: bool,
  highlight: bool,
  animated: bool,
  cardName: string,
  animationDelay: number,
  value: number,
  scope: string,
  className: string,
};

CardMultiplier.defaultProps = {
  isHandheld: false,
  highlight: false,
  animated: true,
  cardName: BACK_CARD_NAME,
  value: '',
  scope: '',
  animationDelay: 1.5,
};

export default memo(CardMultiplier);
